import { useEffect, useState, useMemo } from 'react';
import API from '../api/api';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Briefcase,
  Users,
  Building2,
  Layers,
  MapPin,
  LayoutGrid,
  Table as TableIcon,
} from 'lucide-react';
import './JobInformationPage.css'; // gradient animation

const JobInformationPage = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [service, setService] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // "grid" or "table"
  const [filter, setFilter] = useState('latest'); // latest, oldest, job, test
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, customersRes, clientsRes, serviceRes, projectsRes] =
          await Promise.all([
            API.get('/jobs/'),
            API.get('/customer/'),
            API.get('/client/'),
            API.get('/service/'),
            API.get('/projects/'),
          ]);

        setJobs(jobsRes.data || []);
        setCustomers(customersRes.data || []);
        setClients(clientsRes.data || []);
        setService(serviceRes.data || []);
        setProjects(projectsRes.data || []);
      } catch (err) {
        toast.error('Failed to load data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getCustomerName = (id) => {
    const customer = customers.find((c) => c.customer_id === id);
    return customer ? customer.customer_name : '-';
  };

  const getClientName = (id) => {
    const client = clients.find((c) => c.client_id === id);
    return client ? client.client_name : '-';
  };

  const getServiceName = (id) => {
    const serviceItem = service.find((s) => s.service_id === id);
    return serviceItem ? serviceItem.service_name : '-';
  };

  const getProjectName = (id) => {
    const project = projects.find((p) => p.id === id);
    return project ? project.project_id : '-';
  };

const filteredJobs = useMemo(() => {
  // Map project ID to project object for easy access
  const jobsWithProject = jobs.map((job) => {
    const projectObj = projects.find((p) => p.id === job.project);
    return { ...job, projectObj };
  });

  // Filter by search query
  let filtered = jobsWithProject.filter((job) => {
    const query = searchQuery.toLowerCase();
    return (
      (job.job_number ?? '').toLowerCase().includes(query) ||
      getCustomerName(job.customer_name).toLowerCase().includes(query) ||
      getClientName(job.client_name).toLowerCase().includes(query) ||
      (job.projectObj?.project_id ?? '').toLowerCase().includes(query) ||
      (job.well_name ?? '').toLowerCase().includes(query)
    );
  });

  // Filter by type
  if (filter === 'job' || filter === 'test') {
    filtered = filtered.filter(
      (job) => job.projectObj?.project_type?.toLowerCase() === filter
    );
  }

  // Sort by date
  filtered.sort((a, b) => {
    const dateA = new Date(a.start_date || a.created_date);
    const dateB = new Date(b.start_date || b.created_date);
    if (filter === 'oldest') return dateA - dateB;
    return dateB - dateA; // latest first default
  });

  return filtered;
}, [jobs, projects, customers, clients, searchQuery, filter]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-10 px-6 relative">
      <Toaster />
      <div className="max-w-7xl mx-auto">
        {/* Header & Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
          <h2 className="text-2xl font-bold text-white px-4 py-2 rounded-xl shadow bg-gradient-to-r from-red-600 to-gray-700">
            Job Informations
          </h2>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Search */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-2xl dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 rounded-2xl border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 text-sm"
            >
              <option value="latest">Latest Job</option>
              <option value="oldest">Oldest Job</option>
              <option value="job">Job</option>
              <option value="test">Test</option>
            </select>

            {/* Toggle Button */}
            <button
              onClick={() =>
                setViewMode(viewMode === 'grid' ? 'table' : 'grid')
              }
              className="p-2 rounded-xl text-white shadow bg-gradient-to-r from-red-600 to-gray-700 hover:from-red-700 hover:to-gray-800 transition"
            >
              {viewMode === 'grid' ? <TableIcon size={18} /> : <LayoutGrid size={18} />}
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <p className="text-center text-gray-600 dark:text-gray-300">
            Loading...
          </p>
        ) : filteredJobs.length > 0 ? (
          <>
            {viewMode === 'grid' ? (
              // Grid / Card View
              <motion.div
                layout
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filteredJobs.map((job) => (
                  <motion.div
                    key={job.id}
                    layout
                    className="relative p-5 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer group transition-all duration-300 overflow-hidden"
                    onClick={() => navigate(`/jobdetails/${job.id}`)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-tr from-red-600 to-gray-900 opacity-0 group-hover:opacity-100 transition-all duration-500 animate-gradient"></div>

                    <div className="relative z-10 text-gray-700 dark:text-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-red-600 to-gray-700 text-white shadow">
                          {job.job_number}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-white">
                          {job.start_date}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <p className="flex items-center gap-2 group-hover:text-white">
                          <Users size={16} /> {getCustomerName(job.customer_name)}
                        </p>
                        <p className="flex items-center gap-2 group-hover:text-white">
                          <Building2 size={16} /> {getClientName(job.client_name)}
                        </p>
                        <p className="flex items-center gap-2 group-hover:text-white">
                          <Briefcase size={16} /> {getServiceName(job.service_name)}
                        </p>
                        <p className="flex items-center gap-2 group-hover:text-white">
                          <Layers size={16} /> {getProjectName(job.project)}
                        </p>
                        <p className="flex items-center gap-2 group-hover:text-white">
                          <MapPin size={16} /> {job.well_name ?? '-'}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              // Table View
              <div className="overflow-x-auto rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 max-h-[800px]">
                <table className="w-full text-left text-sm border-collapse">
                  <thead className="bg-gradient-to-r from-red-600 to-gray-700 text-white sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 font-medium text-left">Job Number</th>
                      <th className="px-4 py-3 font-medium text-left">Customer</th>
                      <th className="px-4 py-3 font-medium text-left">Client</th>
                      <th className="px-4 py-3 font-medium text-left">Service</th>
                      <th className="px-4 py-3 font-medium text-left">Project</th>
                      <th className="px-4 py-3 font-medium text-left">Well Name</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800">
                    {filteredJobs.map((job, idx) => (
                      <tr
                        key={job.id}
                        className={`cursor-pointer transition-all duration-150 ${
                          idx % 2 === 0
                            ? 'bg-white dark:bg-gray-800'
                            : 'bg-gray-50 dark:bg-gray-700'
                        } hover:scale-[1.01] hover:shadow-lg`}
                        onClick={() => navigate(`/jobdetails/${job.id}`)}
                      >
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                          {job.job_number ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                          {getCustomerName(job.customer_name)}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                          {getClientName(job.client_name)}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                          {getServiceName(job.service_name)}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                          {getProjectName(job.project)}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                          {job.well_name ?? '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            No matching jobs found.
          </div>
        )}
      </div>
    </div>
  );
};

export default JobInformationPage;
